import { IntegrationStatus } from '@idearoom/types';
import { Dispatch } from 'redux';
import { TFunction } from 'i18next';
import { MenuIconKey } from '../components/MenuIcon';
import { ConfiguratorEnabledOnProps } from '../types/Configurator';
import { unknownUser } from '../types/User';
import { ViewerState } from '../types/ViewerState';
import {
  isCurrentUserAdmin,
  isCurrentUserManager,
  isCurrentUserSuperUser,
  isIdeaRoomGroup,
  isIdeaRoomUser,
} from '../utils/userUtils';
import { getEnabledOnProperty } from '../utils/vendorDataUtils';
import { I18nKeys } from './I18nKeys';
import { canUsePayments } from '../utils/permissionUtils';
import { CurrentUserState } from '../types/CurrentUserState';
import { SupplierDataCategory, VendorDataCategory } from './VendorData';
import { MainMenuItems } from './MainMenuItems';
import {
  BusinessInfoFormFields,
  ColorsAndLogosFormFields,
  LeadCaptureFormFields,
  BannersAndMessagesFormFields,
  DocumentsFormFields,
  FloorplanFormFields,
  ControlsAndFeaturesFormFields,
  VisibilityFormFields,
} from './FormFields';
import { snakeCaseToCamelCase } from '../utils/stringUtils';
import { hasSuppliers } from '../utils/supplierUtils';
import { isCarportView, mapClientAndSupplierToClientSupplier } from '../utils/clientIdUtils';
import { PricingState } from '../types/PricingState';
import { CLIENT_UPDATE_CATEGORY_MAPPING } from './PricingClientUpdate';
import { ClientDataBranch } from './ClientDataBranch';

export type MenuProps = {
  clientId: string;
  selectedGroupId: string;
  configurators: any[];
  viewerState: ViewerState;
  currentUserState: CurrentUserState;
  pricingState: PricingState;
  pricingEnableClientManaged: boolean;
};

type SearchProps = {
  visible: (props: MenuProps) => boolean;
  label?: (props: MenuProps, t: TFunction) => string;
};

type MainMenuSection = MainMenuItem[];
type SecondaryMenuSection = {
  label: (props: MenuProps, t: TFunction) => string;
  items: SecondaryMenuItem[];
};

type SecondaryMenuItem = {
  id?: string;
  visible?: (props: MenuProps) => boolean;
  label: (props: MenuProps, t: TFunction) => string;
  ideaRoomOnly?: (props: MenuProps) => boolean;
  fields?: string[];
  search?: SearchProps;
  branch?: ClientDataBranch;
};

type MainMenuItem = {
  id: MainMenuItems;
  visible: (props: MenuProps) => boolean;
  label: (props: MenuProps, t: TFunction) => string;
  ideaRoomOnly: (props: MenuProps) => boolean;
  primaryIcon?: (props: MenuProps) => MenuIconKey | undefined;
  secondaryIcon?: (props: MenuProps) => MenuIconKey | undefined;
  subMenu?: (props: MenuProps, dispatch: Dispatch<any>) => SecondaryMenuItem[];
  secondaryMenu?: SecondaryMenuSection[];
  search?: SearchProps;
};

export const mainMenu: MainMenuSection[] = [
  [
    {
      id: MainMenuItems.Home,
      visible: ({ selectedGroupId, configurators }) =>
        !isIdeaRoomGroup(selectedGroupId) && configurators && configurators[0]?.vendorData?.vendor?.maxioSubscriptionId,
      label: (_props: MenuProps, t: TFunction) => t(I18nKeys.MenuHomeOption),
      ideaRoomOnly: () => false,
      primaryIcon: () => MenuIconKey.HOME,
    },
    {
      id: MainMenuItems.Sales,
      visible: ({ selectedGroupId }: MenuProps) => !isIdeaRoomGroup(selectedGroupId),
      label: (_props: MenuProps, t: TFunction) => t(I18nKeys.MenuSalesOption),
      ideaRoomOnly: () => false,
      primaryIcon: () => MenuIconKey.SALES,
      subMenu: () => [
        {
          id: MainMenuItems.Leads,
          visible: ({ selectedGroupId }: MenuProps) => !isIdeaRoomGroup(selectedGroupId),
          label: (_props: MenuProps, t: TFunction) => t(I18nKeys.MenuLeadsOption),
          ideaRoomOnly: () => false,
          search: {
            visible: () => true,
            label: () => 'leads',
          },
        },
        {
          id: MainMenuItems.Orders,
          visible: ({ selectedGroupId, currentUserState: { user = unknownUser } }: MenuProps) =>
            !isIdeaRoomGroup(selectedGroupId) && isIdeaRoomUser(user),
          label: (_props: MenuProps, t: TFunction) => t(I18nKeys.MenuOrdersOption),
          ideaRoomOnly: () => true,
          search: {
            visible: () => true,
            label: () => 'orders',
          },
        },
        {
          id: MainMenuItems.Payments,
          visible: ({
            selectedGroupId,
            currentUserState: { user = unknownUser },
            viewerState: { paymentIntegrationStatus },
          }: MenuProps) => !isIdeaRoomGroup(selectedGroupId) && canUsePayments(user, paymentIntegrationStatus?.status),
          label: (_props: MenuProps, t: TFunction) => t(I18nKeys.MenuPaymentsOption),
          ideaRoomOnly: () => false,
          secondaryIcon: (viewerState: ViewerState) => {
            switch (viewerState.paymentIntegrationStatus?.status) {
              case IntegrationStatus.Disconnected:
                return MenuIconKey.NEW;
              case IntegrationStatus.Pending:
                return MenuIconKey.ATTENTION;
              case IntegrationStatus.Failed:
                return MenuIconKey.WARNING;
              default:
                return undefined;
            }
          },
          search: {
            visible: ({ viewerState: { paymentIntegrationStatus }, currentUserState: { user = unknownUser } }) =>
              canUsePayments(user, paymentIntegrationStatus?.status) &&
              [IntegrationStatus.Connected, IntegrationStatus.Deactivated].includes(
                paymentIntegrationStatus?.status || IntegrationStatus.Disconnected,
              ),
            label: () => 'payments',
          },
        },
      ],
    },
    {
      id: MainMenuItems.Sites,
      visible: ({ selectedGroupId, currentUserState: { user = unknownUser } }) =>
        !isIdeaRoomGroup(selectedGroupId) && (isIdeaRoomUser(user) || isCurrentUserAdmin(user)),
      label: (_props: MenuProps, t: TFunction) => t(I18nKeys.MenuSitesOption),
      ideaRoomOnly: () => false,
      primaryIcon: () => MenuIconKey.SITES,
      secondaryMenu: [
        {
          label: () => 'Site',
          items: [
            {
              id: VendorDataCategory.BusinessInfo,
              label: () => 'Business Info',
              fields: Object.values(BusinessInfoFormFields).map((field) => snakeCaseToCamelCase(field)),
              branch: ClientDataBranch.SiteDetail,
            },
            {
              id: VendorDataCategory.ColorsAndLogos,
              label: () => 'Colors & Logos',
              fields: Object.values(ColorsAndLogosFormFields).map((field) => snakeCaseToCamelCase(field)),
              branch: ClientDataBranch.SiteDetail,
            },
            {
              id: VendorDataCategory.LeadCapture,
              label: () => 'Lead Capture',
              fields: Object.values(LeadCaptureFormFields).map((field) => snakeCaseToCamelCase(field)),
              branch: ClientDataBranch.SiteDetail,
            },
            {
              id: VendorDataCategory.BannersAndMessages,
              label: () => 'Banners & Messages',
              fields: Object.values(BannersAndMessagesFormFields).map((field) => snakeCaseToCamelCase(field)),
              branch: ClientDataBranch.SiteDetail,
            },
            {
              id: VendorDataCategory.Documents,
              label: () => 'Documents',
              fields: Object.values(DocumentsFormFields).map((field) => snakeCaseToCamelCase(field)),
              branch: ClientDataBranch.SiteDetail,
            },
            {
              id: VendorDataCategory.Floorplan,
              label: () => 'Floorplan',
              fields: Object.values(FloorplanFormFields).map((field) => snakeCaseToCamelCase(field)),
              branch: ClientDataBranch.SiteDetail,
            },
            {
              id: VendorDataCategory.ControlsAndFeatures,
              label: () => 'Controls & Features',
              fields: Object.values(ControlsAndFeaturesFormFields).map((field) => snakeCaseToCamelCase(field)),
              branch: ClientDataBranch.SiteDetail,
            },
            {
              id: VendorDataCategory.Visibility,
              label: () => 'Visibility',
              fields: Object.values(VisibilityFormFields).map((field) => snakeCaseToCamelCase(field)),
              branch: ClientDataBranch.SiteDetail,
            },
          ],
        },
      ],
      subMenu: ({ configurators }: MenuProps) =>
        configurators.map(({ clientId = '', name = '' }) => ({
          id: clientId,
          label: () => name || clientId,
        })),
    },
    {
      id: MainMenuItems.Pricing,
      visible: ({ selectedGroupId, currentUserState: { user = unknownUser }, configurators }: MenuProps) =>
        !isIdeaRoomGroup(selectedGroupId) &&
        (isCurrentUserAdmin(user) || isIdeaRoomUser(user)) &&
        configurators.some((c) => getEnabledOnProperty(c.vendorData, ConfiguratorEnabledOnProps.PricingEnabled, true)),
      label: () => 'Product Catalogs',
      ideaRoomOnly: () => false,
      primaryIcon: () => MenuIconKey.CATALOG,
      secondaryMenu: [
        {
          label: () => 'Sales Operations',
          items: [
            {
              id: VendorDataCategory.Surcharges,
              label: () => 'Discounts & Surcharges',
              branch: ClientDataBranch.PricingSurcharge,
            },
          ],
        },
        {
          label: () => 'Prices',
          items: [
            {
              id: SupplierDataCategory.BasePrices,
              label: () => 'Base Prices',
              visible: ({
                clientId,
                configurators,
                currentUserState: { user = unknownUser },
                pricingEnableClientManaged,
              }: MenuProps) =>
                (isIdeaRoomUser(user) || !!pricingEnableClientManaged) &&
                !hasSuppliers(configurators.find((c) => c.clientId === clientId)),
              search: {
                visible: () => true,
                label: () => 'price sheets',
              },
              branch: ClientDataBranch.Pricing,
            },
            {
              id: SupplierDataCategory.SizeBased,
              label: () => 'Height & Walls',
              visible: ({ clientId, configurators }) =>
                isCarportView(clientId) && !hasSuppliers(configurators.find((c) => c.clientId === clientId)),
              search: {
                visible: () => true,
                label: (
                  {
                    pricingState: {
                      sizeBased: { selectedCategoryKey },
                    },
                  },
                  t,
                ) =>
                  selectedCategoryKey
                    ? t(CLIENT_UPDATE_CATEGORY_MAPPING[selectedCategoryKey].label).toLocaleLowerCase()
                    : 'categories',
              },
              branch: ClientDataBranch.Pricing,
            },
            {
              id: SupplierDataCategory.Components,
              label: () => 'Components',
              visible: ({ clientId, configurators }) =>
                !hasSuppliers(configurators.find((c) => c.clientId === clientId)),
              search: {
                visible: () => true,
                label: (
                  {
                    pricingState: {
                      component: { selectedCategoryKey },
                    },
                  },
                  t,
                ) =>
                  selectedCategoryKey
                    ? t(CLIENT_UPDATE_CATEGORY_MAPPING[selectedCategoryKey].label).toLocaleLowerCase()
                    : 'categories',
              },
              branch: ClientDataBranch.Pricing,
            },
          ],
        },
      ],
      subMenu: ({ configurators }: MenuProps) =>
        configurators.flatMap((config) =>
          hasSuppliers(config)
            ? config.suppliers.map(({ supplierName = '', supplierKey = '' }) => ({
                id: mapClientAndSupplierToClientSupplier(config.clientId, supplierKey),
                label: () => supplierName || supplierKey,
              }))
            : {
                id: config.clientId,
                label: () => config.name || config.clientId,
              },
        ),
    },
    {
      id: MainMenuItems.ClientData,
      visible: ({ currentUserState: { user = unknownUser } }: MenuProps) =>
        isIdeaRoomUser(user) || isCurrentUserSuperUser(user),
      label: (_props: MenuProps, t: TFunction) => t(I18nKeys.MenuClientDataOption),
      ideaRoomOnly: () => false,
      primaryIcon: () => MenuIconKey.SITES,
    },
    {
      id: MainMenuItems.Analyze,
      visible: ({ selectedGroupId, currentUserState: { user = unknownUser } }: MenuProps) =>
        !isIdeaRoomGroup(selectedGroupId) && (isIdeaRoomUser(user) || isCurrentUserAdmin(user)),
      label: (_props: MenuProps, t: TFunction) => t(I18nKeys.MenuUsageOption),
      ideaRoomOnly: () => false,
      primaryIcon: () => MenuIconKey.ANALYZE,
      subMenu: ({ configurators }: MenuProps) =>
        configurators.map(({ clientId = '', name = '' }) => ({
          id: clientId,
          label: () => name || clientId,
        })),
    },
    {
      id: MainMenuItems.Groups,
      visible: ({ selectedGroupId }: MenuProps) => isIdeaRoomGroup(selectedGroupId),
      label: (_props: MenuProps, t: TFunction) => t(I18nKeys.MenuGroupsOption),
      ideaRoomOnly: () => false,
      primaryIcon: () => MenuIconKey.TEAM,
      search: {
        visible: () => true,
        label: () => 'groups',
      },
    },
  ],
  [
    {
      id: MainMenuItems.Team,
      visible: ({ currentUserState: { user = unknownUser } }: MenuProps) =>
        isIdeaRoomUser(user) || isCurrentUserAdmin(user) || isCurrentUserManager(user),
      label: ({ selectedGroupId }: MenuProps, t: TFunction) =>
        isIdeaRoomGroup(selectedGroupId) ? 'All Users' : t(I18nKeys.MenuTeamOption),
      ideaRoomOnly: () => false,
      primaryIcon: () => MenuIconKey.TEAM,
      search: {
        visible: () => true,
        label: () => 'team members',
      },
    },
    {
      id: MainMenuItems.Dealers,
      visible: ({ currentUserState: { user = unknownUser }, configurators }: MenuProps) =>
        configurators.some((c) =>
          getEnabledOnProperty(c.vendorData, ConfiguratorEnabledOnProps.DealerNetworkEnabled, true),
        ) &&
        (isIdeaRoomUser(user) || isCurrentUserAdmin(user)),
      label: (_props: MenuProps, t: TFunction) => t(I18nKeys.MenuDealersOption),
      ideaRoomOnly: () => false,
      primaryIcon: () => MenuIconKey.DEALERS,
      subMenu: ({ configurators }: MenuProps) =>
        configurators
          .filter((c) => getEnabledOnProperty(c.vendorData, ConfiguratorEnabledOnProps.DealerNetworkEnabled, true))
          .map(({ clientId = '', name = '' }) => ({
            id: clientId,
            label: () => name || clientId,
          })),
      search: {
        visible: () => true,
        label: () => 'dealers',
      },
    },
    {
      id: MainMenuItems.Integrations,
      visible: ({ selectedGroupId, currentUserState: { user = unknownUser } }: MenuProps) =>
        !isIdeaRoomGroup(selectedGroupId) && (isIdeaRoomUser(user) || isCurrentUserAdmin(user)),
      label: (_props: MenuProps, t: TFunction) => t(I18nKeys.MenuIntegrationsOption),
      ideaRoomOnly: () => false,
      primaryIcon: () => MenuIconKey.INTEGRATIONS,
    },
    {
      id: MainMenuItems.Reports,
      visible: ({ selectedGroupId }: MenuProps) => isIdeaRoomGroup(selectedGroupId),
      label: (_props: MenuProps, t: TFunction) => t(I18nKeys.MenuReportsOption),
      ideaRoomOnly: () => false,
      primaryIcon: () => MenuIconKey.REPORTS,
    },
  ],
  [
    {
      id: MainMenuItems.Settings,
      visible: ({ selectedGroupId, currentUserState: { user = unknownUser } }: MenuProps) =>
        /* TODO: Add back in commented line after soft release */
        /* {(isIdeaRoomUser(user) || isCurrentUserAdmin(user)) && ( */
        isIdeaRoomUser(user) ||
        (['EagleCarports', 'Hillhout', 'IdeaRoom'].includes(selectedGroupId) && isCurrentUserAdmin(user)),
      label: (_props: MenuProps, t: TFunction) => t(I18nKeys.MenuSettingsOption),
      ideaRoomOnly: ({ selectedGroupId, currentUserState: { user = unknownUser } }: MenuProps) =>
        isIdeaRoomUser(user) && !['EagleCarports', 'Hillhout', 'IdeaRoom'].includes(selectedGroupId),
      primaryIcon: () => MenuIconKey.SETTINGS,
    },
    {
      id: MainMenuItems.Help,
      visible: ({ selectedGroupId }: MenuProps) => !isIdeaRoomGroup(selectedGroupId),
      label: (_props: MenuProps, t: TFunction) => t(I18nKeys.MenuHelpOption),
      ideaRoomOnly: () => false,
      primaryIcon: () => MenuIconKey.HELP,
    },
  ],
];
